import React from 'react';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, Target, Users, Shield, Zap, ArrowRight, 
  Brain, Bell, Clock, CheckCircle, Sparkles, BarChart3,
  Globe, Award, HeartHandshake
} from 'lucide-react';
import { motion } from 'framer-motion';
import SEO, { SEOConfigs, OrganizationStructuredData } from '../../components/SEO';

const HakkimizdaPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <>
      <SEO
        {...SEOConfigs.hakkimizda}
        structuredData={OrganizationStructuredData}
      />
      
      <div className="min-h-screen bg-brand-dark">
        {/* Hero Section */}
        <section className="relative pt-24 pb-16 px-6 overflow-hidden">
          {/* Background Gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
          
          <div className="container mx-auto max-w-5xl relative">
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6">
                <Brain className="h-4 w-4 text-primary" />
                <span className="text-sm text-primary font-medium">Yapay Zeka Destekli</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Trading'de <span className="text-primary">Zamanlamayı</span><br />
                Yapay Zekaya Bırak
              </h1>
              
              <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto mb-4">
                Borsamex, yapay zeka algoritmaları ile piyasaları analiz eder ve 
                <span className="text-primary font-medium"> AL, SAT veya BEKLE</span> kararlarını sizin için üretir.
              </p>
              
              <p className="text-base text-slate-400 max-w-2xl mx-auto">
                Artık saatlerce grafik analizi yapmak zorunda değilsiniz. 
                Sinyal geldiğinde telefonunuza bildirim düşer, siz kararı verirsiniz.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-16 px-6 border-t border-white/5">
          <div className="container mx-auto max-w-5xl">
            <motion.div 
              className="grid md:grid-cols-2 gap-12 items-center"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.div variants={fadeInUp}>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 border border-blue-500/20 rounded-full mb-4">
                  <Target className="h-3.5 w-3.5 text-blue-400" />
                  <span className="text-xs text-blue-400 font-medium">Misyonumuz</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  Herkes için Erişilebilir<br />Trading Zekası
                </h2>
                <p className="text-slate-400 mb-6">
                  Trading dünyasında başarılı olmak için artık Wall Street'te çalışmanıza gerek yok. 
                  Borsamex, profesyonel seviyede analiz araçlarını herkesin cebine taşıyor.
                </p>
                <div className="space-y-3">
                  {[
                    'Karmaşık teknik analizleri sadeleştiriyoruz',
                    'Anlık bildirimlerle fırsatları kaçırmazsınız',
                    'Şeffaf performans verileri ile güven inşa ediyoruz'
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                      <span className="text-slate-300 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
              
              <motion.div variants={fadeInUp} className="relative">
                <div className="bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/10 rounded-2xl p-8">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="text-center p-4 bg-white/[0.02] rounded-xl">
                      <div className="text-3xl font-bold text-primary mb-1">7/24</div>
                      <div className="text-xs text-slate-400">Piyasa Takibi</div>
                    </div>
                    <div className="text-center p-4 bg-white/[0.02] rounded-xl">
                      <div className="text-3xl font-bold text-blue-400 mb-1">AI</div>
                      <div className="text-xs text-slate-400">Destekli Analiz</div>
                    </div>
                    <div className="text-center p-4 bg-white/[0.02] rounded-xl">
                      <div className="text-3xl font-bold text-amber-400 mb-1">%100</div>
                      <div className="text-xs text-slate-400">Şeffaflık</div>
                    </div>
                    <div className="text-center p-4 bg-white/[0.02] rounded-xl">
                      <div className="text-3xl font-bold text-purple-400 mb-1">TR</div>
                      <div className="text-xs text-slate-400">Yerli Platform</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 px-6 bg-gradient-to-b from-white/[0.01] to-transparent">
          <div className="container mx-auto max-w-5xl">
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                Nasıl Çalışır?
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Basit, şeffaf ve etkili bir sinyal sistemi
              </p>
            </motion.div>

            <motion.div 
              className="grid md:grid-cols-3 gap-6"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              {[
                {
                  icon: Brain,
                  color: 'primary',
                  title: 'AI Analiz Eder',
                  desc: 'Yapay zeka algoritmaları 7/24 piyasaları tarar, teknik göstergeleri ve fiyat hareketlerini analiz eder.',
                  detail: 'RSI, MACD, Bollinger, hacim analizi ve daha fazlası'
                },
                {
                  icon: Bell,
                  color: 'blue-400',
                  title: 'Sinyal Üretir',
                  desc: 'Fırsat tespit edildiğinde AL veya SAT sinyali oluşturulur. Giriş fiyatı, hedefler ve stop-loss belirlenir.',
                  detail: 'Net ve uygulanabilir trading planı'
                },
                {
                  icon: Zap,
                  color: 'amber-400',
                  title: 'Siz Karar Verirsiniz',
                  desc: 'Sinyal telefonunuza ulaşır. İşlemi açıp açmamak tamamen size kalmış - kontrol sizde.',
                  detail: 'Telegram, Push ve E-posta bildirimleri'
                }
              ].map((item, i) => (
                <motion.div 
                  key={i}
                  variants={fadeInUp}
                  className="relative"
                >
                  <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-6 h-full hover:border-white/10 transition-colors">
                    <div className="absolute -top-3 -left-3 w-8 h-8 bg-brand-dark border border-white/10 rounded-full flex items-center justify-center text-sm font-bold text-white">
                      {i + 1}
                    </div>
                    <div className={`w-12 h-12 bg-${item.color}/10 rounded-xl flex items-center justify-center mb-4`}>
                      <item.icon className={`h-6 w-6 text-${item.color}`} />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                    <p className="text-sm text-slate-400 mb-3">{item.desc}</p>
                    <p className="text-xs text-slate-500 italic">{item.detail}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Values */}
        <section className="py-16 px-6 border-t border-white/5">
          <div className="container mx-auto max-w-5xl">
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                Değerlerimiz
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Borsamex'i farklı kılan temel prensipler
              </p>
            </motion.div>

            <motion.div 
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-4"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              {[
                {
                  icon: Shield,
                  title: 'Şeffaflık',
                  desc: 'Tüm sinyaller ve sonuçları herkese açık. Gizli agenda yok.',
                  gradient: 'from-primary/10'
                },
                {
                  icon: Award,
                  title: 'Dürüstlük',
                  desc: 'Her sinyal %100 doğru olmaz. Bunu kabul eder, gizlemeyiz.',
                  gradient: 'from-blue-500/10'
                },
                {
                  icon: HeartHandshake,
                  title: 'Kullanıcı Odaklılık',
                  desc: 'Platformu kullanıcı geri bildirimleriyle şekillendiriyoruz.',
                  gradient: 'from-amber-500/10'
                },
                {
                  icon: Sparkles,
                  title: 'Sürekli Gelişim',
                  desc: 'AI modellerimizi sürekli eğitiyor ve iyileştiriyoruz.',
                  gradient: 'from-purple-500/10'
                }
              ].map((item, i) => (
                <motion.div 
                  key={i}
                  variants={fadeInUp}
                  className={`p-5 bg-gradient-to-br ${item.gradient} to-transparent border border-white/5 rounded-xl hover:border-white/10 transition-colors`}
                >
                  <item.icon className="h-8 w-8 text-white/80 mb-3" />
                  <h3 className="text-white font-semibold mb-1.5">{item.title}</h3>
                  <p className="text-sm text-slate-400">{item.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Who We Serve */}
        <section className="py-16 px-6 bg-white/[0.01]">
          <div className="container mx-auto max-w-5xl">
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                Kime Hitap Ediyoruz?
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Borsamex her seviyeden yatırımcı için tasarlandı
              </p>
            </motion.div>

            <motion.div 
              className="grid md:grid-cols-3 gap-6"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              {[
                {
                  icon: Users,
                  title: 'Yeni Başlayanlar',
                  desc: 'Teknik analiz bilmeden, profesyonellerin ne yaptığını takip etmek isteyenler.',
                  features: ['Anlaşılır sinyal formatı', 'Risk yönetimi rehberi', 'Eğitim içerikleri']
                },
                {
                  icon: BarChart3,
                  title: 'Aktif Trader\'lar',
                  desc: 'Zaman kazanmak ve fırsatları kaçırmamak isteyen deneyimli yatırımcılar.',
                  features: ['Anlık bildirimler', 'Çoklu hedef takibi', 'Performans analizi']
                },
                {
                  icon: Clock,
                  title: 'Meşgul Profesyoneller',
                  desc: 'Piyasaları takip edecek zamanı olmayan ama yatırım yapmak isteyenler.',
                  features: ['Otomatik tarama', 'Haftalık özetler', 'Mobil uyumlu platform']
                }
              ].map((item, i) => (
                <motion.div 
                  key={i}
                  variants={fadeInUp}
                  className="bg-white/[0.02] border border-white/5 rounded-2xl p-6 hover:border-primary/20 transition-colors"
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                    <item.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-400 mb-4">{item.desc}</p>
                  <ul className="space-y-2">
                    {item.features.map((feature, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm text-slate-300">
                        <CheckCircle className="h-4 w-4 text-primary/60" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="py-10 px-6 border-t border-white/5">
          <div className="container mx-auto max-w-3xl">
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <Shield className="h-6 w-6 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-white font-semibold mb-2">Önemli Uyarı</h3>
                  <p className="text-sm text-slate-400">
                    Borsamex bir yatırım danışmanlığı hizmeti değildir. Sunulan sinyaller sadece bilgilendirme 
                    amaçlıdır ve yatırım tavsiyesi niteliği taşımaz. Trading yüksek risk içerir ve tüm 
                    sermayenizi kaybedebilirsiniz. Yatırım kararlarınızı vermeden önce kendi araştırmanızı 
                    yapın ve gerekirse profesyonel danışmanlık alın.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-6 border-t border-white/5">
          <div className="container mx-auto max-w-3xl">
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Hemen Başlayın
              </h2>
              <p className="text-slate-400 mb-8 max-w-xl mx-auto">
                Ücretsiz hesap oluşturun ve AI sinyallerinin gücünü keşfedin. 
                Kredi kartı gerekmez.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-brand-dark font-semibold rounded-xl hover:bg-primary/90 transition-colors"
                >
                  Ücretsiz Başla
                  <ArrowRight className="h-5 w-5" />
                </Link>
                <Link
                  to="/nasil-calisir"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 text-white border border-white/10 rounded-xl hover:bg-white/10 transition-colors"
                >
                  Nasıl Çalışır?
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default HakkimizdaPage;
